import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  MenuItem,
} from "@mui/material";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import {
  getGame,
  saveGameSettings,
  saveKnowledgeBaseItems,
  loadKbVideo,
  loadPoster,
  getProfile,
} from "../../redux/asyncThunk/userActions";
import { sanitizePlainText } from "../../utils/inputValidation";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import { getMessengerMeta } from "../../utils/messengers";
import ProjectMessengerAvailability, {
  readAvailableMessengersFromSettings,
} from "../ProjectMessengerAvailability";
import { AUTH_FIELD_SX } from "../../pages/AuthLayout";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import "../trainer/trainer-editor.css";
import "./knowledge-base-editor.css";

const { addMessage } = userSlice;
const fieldSx = { ...AUTH_FIELD_SX, maxWidth: "100%", width: "100%" };

function createEmptyItem() {
  const id = `kb-${Date.now()}`;
  return {
    id,
    question: "",
    answerMode: "text",
    answerText: "",
    hasVideo: false,
    screenpath: "",
  };
}

export default function KnowledgeBaseEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game) ?? initialGame;
  const user = useSelector((state) => state.user?.data?.user);
  const profile = useSelector((state) => state.user?.profile);

  const [title, setTitle] = useState(game?.title ?? "");
  const [kbTheme, setKbTheme] = useState(game?.kbTheme ?? "");
  const [description, setDescription] = useState(game?.description ?? "");
  const [items, setItems] = useState(game?.kbItems ?? []);
  const [activeItemId, setActiveItemId] = useState(null);
  const [settings, setSettings] = useState({
    projectStatus: getProjectStatusFromSettings(game?.settings),
    completion_text: game?.settings?.completion_text ?? "",
    author_telegram: game?.settings?.author_telegram ?? "",
    allow_replay: game?.settings?.allow_replay !== false,
  });
  const [saving, setSaving] = useState(false);

  const availableMessengers = readAvailableMessengersFromSettings(game?.settings);
  const messengerAvailabilityLabel = useMemo(
    () =>
      availableMessengers
        .map((id) => getMessengerMeta(id).label)
        .join(", "),
    [availableMessengers]
  );

  const activeItem = useMemo(
    () => items.find((item) => item.id === activeItemId) ?? null,
    [items, activeItemId]
  );

  useEffect(() => {
    if (user?.id && !profile?.login) {
      dispatch(getProfile({ id: user.id }));
    }
  }, [dispatch, user?.id, profile?.login]);

  useEffect(() => {
    if (!game) return;
    setTitle(game.title ?? "");
    setKbTheme(game.kbTheme ?? "");
    setDescription(game.description ?? "");
    setItems(game.kbItems ?? []);
    setSettings({
      projectStatus: getProjectStatusFromSettings(game.settings),
      completion_text: game.settings?.completion_text ?? "",
      author_telegram: game.settings?.author_telegram ?? "",
      allow_replay: game.settings?.allow_replay !== false,
    });
  }, [game]);

  const updateActiveItem = (patch) => {
    if (!activeItemId) return;
    setItems((prev) =>
      prev.map((item) => (item.id === activeItemId ? { ...item, ...patch } : item))
    );
  };

  const handleAddItem = () => {
    const next = createEmptyItem();
    setItems((prev) => [...prev, next]);
    setActiveItemId(next.id);
  };

  const handleDeleteItem = (itemId) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
    if (activeItemId === itemId) {
      setActiveItemId(null);
    }
  };

  const handleSaveAll = async () => {
    if (!game?._id) return;
    setSaving(true);
    try {
      await dispatch(
        saveGameSettings({
          game: game._id,
          title,
          description,
          program: kbTheme,
          category: "",
          kbTheme,
          settings: mergeGameSettings(game?.settings, {
            ...settingsFromProjectStatus(settings.projectStatus, game?.settings),
            contentType: "knowledge_base",
            completion_text: settings.completion_text,
            author_telegram: settings.author_telegram,
            allow_replay: settings.allow_replay,
          }),
        })
      ).unwrap();

      await dispatch(
        saveKnowledgeBaseItems({
          gameId: game._id,
          kbTheme,
          items,
        })
      ).unwrap();

      dispatch(addMessage({ type: "info", message: "База знаний сохранена" }));
      dispatch(getGame({ gameId: game._id }));
    } catch {
      dispatch(addMessage({ type: "error", message: "Не удалось сохранить" }));
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeItem || !user?.id || !game?._id) return;

    dispatch(
      loadKbVideo({
        userId: user.id,
        gameId: game._id,
        itemId: activeItem.id,
        file,
      })
    ).then((result) => {
      if (result.payload?.kbItems) {
        setItems(result.payload.kbItems);
      }
    });
  };

  const handlePoster = (event, gameId) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      dispatch(
        loadPoster({
          gameId,
          base64String: reader.result,
          onSucces: () => dispatch(getGame({ gameId })),
        })
      );
    };
  };

  return (
    <div className="trainer-editor kb-editor">
      <div className="trainer-editor__toolbar">
        <div>
          <h2 className="trainer-editor__heading">
            {title || "База знаний"}
          </h2>
          {kbTheme ? (
            <p className="trainer-editor__subheading">Тема: {kbTheme}</p>
          ) : null}
        </div>
        <div className="trainer-editor__toolbar-actions">
          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            disabled={saving}
            onClick={handleSaveAll}
          >
            Сохранить
          </Button>
        </div>
      </div>

      <div className="trainer-editor__layout kb-editor__layout">
        <div className="trainer-editor__form">
          <section className="trainer-editor__card">
            <h3>Настройки базы знаний</h3>
            <TextField
              label="Название"
              size="small"
              fullWidth
              sx={fieldSx}
              value={title}
              onChange={(e) => setTitle(sanitizePlainText(e.target.value, 120))}
            />
            <TextField
              label="Тема"
              size="small"
              fullWidth
              sx={fieldSx}
              value={kbTheme}
              onChange={(e) => setKbTheme(sanitizePlainText(e.target.value, 200))}
              placeholder="Например: адаптация новых сотрудников"
            />
            <TextField
              label="Приветственный текст"
              size="small"
              fullWidth
              multiline
              minRows={3}
              sx={fieldSx}
              value={description}
              onChange={(e) =>
                setDescription(sanitizePlainText(e.target.value, 2000))
              }
            />
            <TextField
              select
              label="Статус"
              size="small"
              sx={fieldSx}
              value={settings.projectStatus}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  projectStatus: e.target.value,
                }))
              }
            >
              <MenuItem value="draft">Черновик</MenuItem>
              <MenuItem value="published">Опубликован</MenuItem>
              <MenuItem value="hidden">Скрыт</MenuItem>
            </TextField>
            <TextField
              label="Telegram HR / автора"
              size="small"
              fullWidth
              sx={fieldSx}
              value={settings.author_telegram}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  author_telegram: sanitizePlainText(e.target.value, 80),
                }))
              }
            />
            <div className="kb-editor__poster">
              <InputFileUpload
                label="Обложка"
                accept="image/*"
                onChange={(event) => handlePoster(event, game._id)}
              />
            </div>
            <ProjectMessengerAvailability
              gameId={game._id}
              value={availableMessengers}
              readOnly
              compact
              showShareLinks
            />
          </section>

          <section className="trainer-editor__card">
            <div className="kb-editor__list-header">
              <h3>Вопросы и ответы</h3>
              <Button size="small" startIcon={<AddIcon />} onClick={handleAddItem}>
                Добавить
              </Button>
            </div>
            {items.length === 0 ? (
              <p className="kb-editor__empty">
                Добавьте типовые вопросы сотрудников и ответы на них — текстом или видео.
              </p>
            ) : (
              <ul className="kb-editor__list">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className={`kb-editor__list-item${
                      activeItemId === item.id ? " kb-editor__list-item--active" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="kb-editor__list-btn"
                      onClick={() => setActiveItemId(item.id)}
                    >
                      <MenuBookOutlinedIcon fontSize="small" />
                      {item.question || "Без названия"}
                    </button>
                    <IconButton
                      size="small"
                      aria-label="Удалить"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {activeItem ? (
            <section className="trainer-editor__card">
              <h3>Редактирование вопроса</h3>
              <TextField
                label="Вопрос сотрудника"
                size="small"
                fullWidth
                multiline
                minRows={2}
                sx={fieldSx}
                value={activeItem.question}
                onChange={(e) =>
                  updateActiveItem({
                    question: sanitizePlainText(e.target.value, 500),
                  })
                }
                placeholder="Например: Как оформить отпуск?"
              />

              <span className="app-dialog__label">Формат ответа</span>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={activeItem.answerMode}
                onChange={(_, value) => {
                  if (value) updateActiveItem({ answerMode: value });
                }}
                className="app-dialog__type-toggle"
              >
                <ToggleButton value="text">
                  <TextFieldsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Текст
                </ToggleButton>
                <ToggleButton value="video">
                  <VideocamOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
                  Видео
                </ToggleButton>
              </ToggleButtonGroup>

              {activeItem.answerMode === "text" ? (
                <TextField
                  label="Ответ"
                  size="small"
                  fullWidth
                  multiline
                  minRows={4}
                  sx={{ ...fieldSx, mt: 1.5 }}
                  value={activeItem.answerText}
                  onChange={(e) =>
                    updateActiveItem({
                      answerText: sanitizePlainText(e.target.value, 4000),
                    })
                  }
                  placeholder="Текст ответа, который увидит сотрудник в боте"
                />
              ) : (
                <div className="kb-editor__video-upload">
                  <InputFileUpload
                    label="Видео-ответ"
                    accept="video/*"
                    onChange={handleVideoUpload}
                  />
                  {activeItem.hasVideo ? (
                    <p className="kb-editor__video-status">Видео загружено</p>
                  ) : null}
                </div>
              )}
            </section>
          ) : null}
        </div>

        <aside className="kb-editor__aside">
          <section className="trainer-editor__card kb-editor__preview-card">
            <h3>Как это работает в боте</h3>
            <ol className="kb-editor__steps">
              <li>Сотрудник открывает ссылку и видит тему базы знаний</li>
              <li>Выбирает свой вопрос из списка</li>
              <li>Получает ответ — текстом или видео</li>
              <li>Может выбрать другой вопрос в любой момент</li>
            </ol>
            <p className="kb-editor__preview-note">
              Доступно в: {messengerAvailabilityLabel}
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
